export const SITE_URL = 'https://taaouniaty.com';
export const SITE_NAME = 'Taaouniaty';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/preview.png`;
export const DEFAULT_OG_IMAGE_WIDTH = 512;
export const DEFAULT_OG_IMAGE_HEIGHT = 512;

export const HOME_SEO = {
  fr: {
    title: 'Taaouniaty - Gestion des coopératives | برنامج تسيير التعاونيات',
    description:
      'Taaouniaty est une solution SaaS moderne pour les coopératives et associations. Gérez vos factures, comptes et dépenses facilement dans une seule plateforme.',
  },
  ar: {
    title: 'تعاونيتي - تسيير التعاونيات والجمعيات | Taaouniaty',
    description:
      'تعاونيتي هو حل سحابي حديث لتسيير التعاونيات والجمعيات. إدارة الفواتير والحسابات والمصاريف بسهولة وفي منصة واحدة.',
  },
};

function truncate(text, max = 160) {
  if (!text) return '';
  const clean = String(text).replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trim()}…`;
}

export function buildAbsoluteUrl(path = '/') {
  if (!path || path === '/') return `${SITE_URL}/`;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${normalized}`;
}

export function buildCatalogSeo({ cooperative, catalogConfig, products = [], slug, language = 'fr' }) {
  const coopName = cooperative?.nom || slug;
  const productNames = (products || [])
    .filter((p) => p.is_active !== false)
    .slice(0, 6)
    .map((p) => p.titre)
    .filter(Boolean);

  const baseDescription =
    catalogConfig?.description ||
    cooperative?.description ||
    (language === 'ar'
      ? `اطلب منتجات ${coopName} عبر الإنترنت.`
      : `Commandez les produits de ${coopName} en ligne.`);

  const productHint =
    productNames.length > 0
      ? language === 'ar'
        ? ` المنتجات: ${productNames.join('، ')}.`
        : ` Produits : ${productNames.join(', ')}.`
      : '';

  const deliveryHint =
    language === 'ar' ? ' التوصيل في المغرب عبر تعاونيتي.' : ' Livraison au Maroc via Taaouniaty.';

  const description = truncate(`${baseDescription}${productHint}${deliveryHint}`);

  const image =
    catalogConfig?.catalog_logo_url ||
    catalogConfig?.top_banner_image_url ||
    cooperative?.logo ||
    DEFAULT_OG_IMAGE;

  const title =
    language === 'ar'
      ? `${coopName} - اطلب عبر الإنترنت | كتالوج تعاونيتي`
      : `${coopName} - Commander en ligne | Catalogue Taaouniaty`;

  return {
    title,
    description,
    image,
    url: buildAbsoluteUrl(`/${slug}`),
    type: 'website',
    coopName,
    keywords: [coopName, 'catalogue', 'commande en ligne', 'coopérative', 'Maroc', 'Taaouniaty', ...productNames]
      .filter(Boolean)
      .join(', '),
  };
}

export function buildCatalogJsonLd({ cooperative, catalogConfig, products = [], slug }) {
  const seo = buildCatalogSeo({ cooperative, catalogConfig, products, slug });
  const url = seo.url;
  const logo =
    catalogConfig?.catalog_logo_url ||
    cooperative?.logo ||
    DEFAULT_OG_IMAGE;

  const activeProducts = (products || []).filter((p) => p.is_active !== false).slice(0, 20);

  const business = {
    '@type': ['LocalBusiness', 'OnlineStore'],
    '@id': `${url}#store`,
    name: seo.coopName,
    url,
    image: logo,
    logo,
    description: seo.description,
    ...(cooperative?.adresse
      ? {
          address: {
            '@type': 'PostalAddress',
            streetAddress: cooperative.adresse,
            addressCountry: 'MA',
          },
        }
      : { address: { '@type': 'PostalAddress', addressCountry: 'MA' } }),
    areaServed: { '@type': 'Country', name: 'Morocco' },
    currenciesAccepted: 'MAD',
    priceRange: '$$',
    ...(cooperative?.phone ? { telephone: cooperative.phone } : {}),
    ...(cooperative?.email ? { email: cooperative.email } : {}),
  };

  const itemList = {
    '@type': 'ItemList',
    name: `Produits - ${seo.coopName}`,
    itemListElement: activeProducts.map((product, index) => {
      const price = product.prix != null ? Number(product.prix) : null;
      const image = Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : logo;
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.titre,
          description: truncate(product.description || product.titre, 200),
          image,
          ...(price != null && !Number.isNaN(price)
            ? {
                offers: {
                  '@type': 'Offer',
                  price: price.toFixed(2),
                  priceCurrency: 'MAD',
                  availability: 'https://schema.org/InStock',
                  url,
                },
              }
            : {}),
        },
      };
    }),
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      business,
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: seo.title,
        description: seo.description,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: { '@id': `${url}#store` },
        mainEntity: { '@id': `${url}#store` },
      },
      itemList,
    ],
  };
}

export const MARKETPLACE_HOME_SEO = {
  title: 'Marketplace des coopératives marocaines | Acheter en ligne | Taaouniaty',
  description:
    'Marketplace des coopératives marocaines : commandez en ligne artisanat, coffrets et produits du terroir. Livraison partout au Maroc, paiement à la livraison.',
  keywords:
    'marketplace coopératives marocaines, acheter produits coopératives Maroc, artisanat marocain en ligne, coffrets coopératives, Taaouniaty',
};

export const MARKETPLACE_HOME_FAQ = [
  {
    q: 'C’est quoi le marketplace des coopératives Taaouniaty ?',
    a: 'Taaouniaty est une boutique en ligne des coopératives marocaines : vous parcourez les produits, vous commandez, et vous payez à la livraison partout au Maroc.',
  },
  {
    q: 'Puis-je vraiment acheter en ligne, pas seulement contacter la coopérative ?',
    a: 'Oui. Taaouniaty a un panier, une commande et un paiement à la livraison. Vous n’êtes pas renvoyé uniquement vers WhatsApp.',
  },
  {
    q: 'La livraison est-elle disponible dans tout le Maroc ?',
    a: 'Oui. Les commandes du marketplace Taaouniaty sont livrées partout au Maroc, avec paiement à la livraison.',
  },
];

export const MARKETPLACE_BROWSE_SEO = {
  title: 'Tous nos produits — coffrets, cadeaux, artisanat marocain | Taaouniaty',
  description:
    'Parcourez tous nos produits artisanaux : coffrets, cadeaux et objets faits main par des coopératives marocaines. Livraison au Maroc.',
  keywords:
    'catalogue artisanat marocain, coffrets, cadeaux, produits artisanaux, coopératives marocaines, Taaouniaty',
};

export function productImageAlt(product) {
  const title = (product?.title || '').trim();
  const desc = String(product?.description || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (title && desc) return truncate(`${title} — ${desc}`, 120);
  if (title) return `${title}, produit artisanal des coopératives marocaines`;
  return 'Produit artisanal des coopératives marocaines';
}

export function buildMarketplaceHomeJsonLd() {
  const url = buildAbsoluteUrl('/marketplace');
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: SITE_URL,
        logo: DEFAULT_OG_IMAGE,
        description:
          'Taaouniaty est le marketplace des coopératives marocaines : achat en ligne, livraison au Maroc, paiement à la livraison.',
      },
      {
        '@type': 'OnlineStore',
        '@id': `${url}#store`,
        name: 'Taaouniaty Marketplace',
        url,
        image: DEFAULT_OG_IMAGE,
        description: MARKETPLACE_HOME_SEO.description,
        parentOrganization: { '@id': `${SITE_URL}/#organization` },
        areaServed: { '@type': 'Country', name: 'Morocco' },
        currenciesAccepted: 'MAD',
        paymentAccepted: 'Cash on delivery',
        availableLanguage: ['fr', 'ar'],
      },
      {
        '@type': 'WebSite',
        '@id': `${url}#website`,
        name: 'Marketplace des coopératives marocaines | Taaouniaty',
        url,
        inLanguage: 'fr-MA',
        publisher: { '@id': `${SITE_URL}/#organization` },
        description: MARKETPLACE_HOME_SEO.description,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${url}/browse?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: MARKETPLACE_HOME_SEO.title,
        description: MARKETPLACE_HOME_SEO.description,
        isPartOf: { '@id': `${url}#website` },
        about: { '@id': `${url}#store` },
        mainEntity: { '@id': `${url}#store` },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: MARKETPLACE_HOME_FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

export function buildMarketplaceBrowseJsonLd({ products = [], category = null, path = '/marketplace/browse' } = {}) {
  const url = buildAbsoluteUrl(path);
  const title = category
    ? `${category.name} des coopératives marocaines | Taaouniaty`
    : MARKETPLACE_BROWSE_SEO.title;
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    url,
    description: category?.description || MARKETPLACE_BROWSE_SEO.description,
    isPartOf: { '@id': `${buildAbsoluteUrl('/marketplace')}#website` },
    mainEntity: {
      '@type': 'ItemList',
      name: title,
      itemListElement: products.slice(0, 30).map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.title,
          description: truncate(product.description || product.title, 200),
          image: product.image_url || DEFAULT_OG_IMAGE,
          url: buildAbsoluteUrl(`/marketplace/p/${product.slug}`),
          offers: {
            '@type': 'Offer',
            price: Number(product.price || 0).toFixed(2),
            priceCurrency: product.currency || 'MAD',
            availability: 'https://schema.org/InStock',
            url: buildAbsoluteUrl(`/marketplace/p/${product.slug}`),
          },
        },
      })),
    },
  };
}

export function buildMarketplaceProductJsonLd(product) {
  if (!product) return null;
  const url = buildAbsoluteUrl(`/marketplace/p/${product.slug}`);
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description: truncate(product.description || product.title, 200),
    image: product.image_url || DEFAULT_OG_IMAGE,
    url,
    brand: { '@type': 'Brand', name: product.brand || product.cooperative_name || SITE_NAME },
    ...(product.category_name ? { category: product.category_name } : {}),
    offers: {
      '@type': 'Offer',
      price: Number(product.price || 0).toFixed(2),
      priceCurrency: product.currency || 'MAD',
      availability: 'https://schema.org/InStock',
      url,
    },
  };
  if (product.rating && product.reviews) {
    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(product.rating).toFixed(1),
      reviewCount: Number(product.reviews),
    };
  }
  return data;
}

export function buildMarketplaceCoopJsonLd({ coop, products = [] } = {}) {
  if (!coop?.slug) return null;
  const name = coop.name || coop.slug;
  const url = buildAbsoluteUrl(`/marketplace/c/${coop.slug}`);
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${name} | Boutique des coopératives marocaines | Taaouniaty`,
    url,
    description: `Achetez en ligne les produits de ${name}, coopérative marocaine. Livraison partout au Maroc, paiement à la livraison.`,
    image: coop.image_url || DEFAULT_OG_IMAGE,
    isPartOf: { '@id': `${buildAbsoluteUrl('/marketplace')}#website` },
    about: {
      '@type': 'Organization',
      name,
      url,
      image: coop.image_url || DEFAULT_OG_IMAGE,
    },
    mainEntity: {
      '@type': 'ItemList',
      name: `Produits de ${name}`,
      itemListElement: products.slice(0, 30).map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.title,
          description: truncate(product.description || product.title, 200),
          image: product.image_url || DEFAULT_OG_IMAGE,
          url: buildAbsoluteUrl(`/marketplace/p/${product.slug}`),
          offers: {
            '@type': 'Offer',
            price: Number(product.price || 0).toFixed(2),
            priceCurrency: product.currency || 'MAD',
            availability: 'https://schema.org/InStock',
            url: buildAbsoluteUrl(`/marketplace/p/${product.slug}`),
          },
        },
      })),
    },
  };
}

